<template>
  <div class="h-full">
    <GenericPopUpBannerContainer />
    <Transition name="fade">
      <component :is="layout">
        <template #leftSidebar>
          <RouterView name="leftSidebar" />
        </template>
        <template #default>
          <RouterView />
        </template>
        <template #footer>
          <RouterView name="footer" />
        </template>
      </component>
    </Transition>
    <PWAUpdatePrompt />
    <ChallengePanel />
    <ChallengeRecorder />
    <VueQueryDevtools v-if="showDevtools" button-position="bottom-right" />
    <Transition name="fade">
      <div
        v-if="isLoggingOut"
        class="fixed inset-0 z-200 flex flex-col items-center justify-center gap-4 bg-white/80 backdrop-blur-sm"
        data-cy="logoutLoadingOverlay"
      >
        <GenericLoadingSpinner />
        <p class="text-tm-gray-700 text-base">Du wirst abgemeldet ...</p>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import ChallengePanel from '@components/Challenge/ChallengePanel.vue';
import ChallengeRecorder from '@components/Challenge/ChallengeRecorder.vue';
import PWAUpdatePrompt from '@components/PWAUpdatePrompt.vue';
import { useAvatarSource } from '@/composables/useAvatarSource';
import { useHistoryIntercept } from '@/composables/useHistoryIntercept';
import { isLoggingOut } from '@composables/session';
import { useKeyboardEventStore } from '@stores/keyboardEventStore';
import { useToastStore } from '@stores/toastStore';
import { AvatarSourceKey, GenericLoadingSpinner } from '@therame/ui';
import VueQueryDevtools from '@tanstack/vue-query-devtools/production';
import { computed, defineAsyncComponent, provide } from 'vue';
import { useRoute } from 'vue-router';
import GenericPopUpBannerContainer from '@components/Notifications/GenericPopUpBannerContainer.vue';

const isChallengeActive = window.localStorage.getItem('challenge_active') === 'true';
const showDevtools =
  ['LOCAL', 'DEVELOPMENT'].includes(import.meta.env.VITE_MODE) && !isChallengeActive;

const route = useRoute();
const toastStore = useToastStore();
const keyboardEventStore = useKeyboardEventStore();

useHistoryIntercept();

// Where `@therame/ui` gets a person's picture from (ADR-0048), once for the whole app.
provide(AvatarSourceKey, useAvatarSource());

const LayoutBasic = defineAsyncComponent(() => import('./layouts/LayoutBasic.vue'));
const LayoutNone = defineAsyncComponent(() => import('./layouts/LayoutNone.vue'));
const LayoutDefault = defineAsyncComponent(() => import('./layouts/LayoutDefault.vue'));

const layout = computed(() => {
  if (route.meta.layout === 'basic') return LayoutBasic;
  if (route.meta.layout === 'none') return LayoutNone;
  return LayoutDefault;
});

document.addEventListener('keydown', (ev) => keyboardEventStore.keyboardEventTrigger.next(ev));

let offlineToastId: string | undefined;

const onOffline = () => {
  if (route.meta.layout === 'basic' && offlineToastId === undefined) {
    offlineToastId = toastStore.addToast({
      text: 'Keine Internetverbindung',
      type: 'error',
      buttonText: 'Neu laden',
      buttonAction: () => window.location.reload(),
      persist: true,
    });
  }
};

const onOnline = () => {
  if (offlineToastId !== undefined) {
    toastStore.dismissToast(offlineToastId);
    offlineToastId = undefined;
  }
};

window.addEventListener('offline', onOffline);
window.addEventListener('online', onOnline);
</script>
<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
